import currency from "currency.js";
import moment from "moment";
import { TFunction } from "i18next";

import {
  GetNotifications200Response,
  InstrumentTypeResponse,
} from "../../../codegen-api";
import { getBrandIconUrl, getERC20Addresses } from "../../../config/chains";
import { getOnchainTokenDecimals } from "../../../config/tokenDecimals";
import {
  getAssetShortName,
  getCollateralAssetByAddress,
  getCollateralPrecision,
} from "../../../utils/asset/assets";
import { fromWei } from "../../../utils/format";
import { instrumentLabel } from "../../../utils/instrumentLabel";
import { fillInstrumentType } from "../../../utils/notifications.summary";
import { getNotificationActivitySeconds } from "../../../utils/notifications";
import type { NotificationFilter } from "../../../contexts/NotificationContext";

// Backstop for legacy notifications cached as raw 18-decimal big-ints.
// A real perp price/size will never exceed 1e10 in display units, so anything
// above that threshold is treated as 18-decimal fixed-point and scaled down.
const RAW_18DEC_THRESHOLD = 1e10;

// Per-brand notification icon, resolved by the active chain's logo folder.
export const logo = getBrandIconUrl();
export function normalizeFillNumeric(raw: unknown): number {
  const n = Number(raw ?? 0);
  if (!Number.isFinite(n)) return 0;
  if (Math.abs(n) > RAW_18DEC_THRESHOLD) return n / 1e18;
  return n;
}

export function getInstrumentBaseAsset(instrument?: string): string | undefined {
  const base = String(instrument || "").split("-")[0]?.trim();
  return base || undefined;
}

export function getFillAsset(metadata?: GetNotifications200Response["metadata"]) {
  return metadata?.asset || getInstrumentBaseAsset(metadata?.instrument);
}

/**
 * A fill's market label, by the fill's own vertical (see fillInstrumentType):
 * "BTC-USD PERP" for a perp, "BTC-USDC" for spot. A fill with no instrument
 * name used to be labelled a perp unconditionally.
 */
export function formatPerpFillInstrument(metadata?: GetNotifications200Response["metadata"]) {
  const instrument = String(metadata?.instrument || "").toUpperCase();
  const base = getFillAsset(metadata);
  const displayBase = getAssetShortName(base) || base;
  const instrumentType = fillInstrumentType((metadata || {}) as Record<string, any>);

  if (instrumentType === InstrumentTypeResponse.Perpetual && displayBase) {
    return `${displayBase}-USD PERP`;
  }
  if (instrumentType === InstrumentTypeResponse.Spot && displayBase) {
    return instrumentLabel(displayBase, InstrumentTypeResponse.Spot);
  }

  return instrument || displayBase || "";
}

// Deposit/withdraw/transfer amounts arrive as raw on-chain integers (wei). To
// render them we MUST scale by the token's real on-chain decimals from the
// checked-in decimals map. There is NO fallback: a wrong/guessed decimals
// silently mis-scales (e.g. a 6-decimal amount by 18 -> "0.00"). When the
// token's decimals are unknown for the active chain (e.g. a stale/cross-chain
// notification) we return null so the caller renders no amount rather than a lie.
export function toCollateralDisplayAmount(
  rawAmount: unknown,
  collateralAddress?: string
): number | null {
  const decimals = getOnchainTokenDecimals(collateralAddress);
  if (decimals === undefined) return null;
  return fromWei(String(rawAmount ?? "0"), decimals);
}

// Active-chain USDC address — used by USDC-denominated notifications (fees, vault)
// that don't carry a token address, so they too resolve real on-chain decimals.
export function activeUsdcAddress(): string | undefined {
  return getERC20Addresses()?.usdc;
}

// Scale a raw on-chain amount to human units AND format it with the asset's own
// display precision (getCollateralPrecision: BTC=5, ETH=4, USDC=2). Returns null
// when the token decimals are unknown (see toCollateralDisplayAmount).
export function formatCollateralAmount(
  rawAmount: unknown,
  collateralAddress?: string
): string | null {
  const human = toCollateralDisplayAmount(rawAmount, collateralAddress);
  if (human === null) return null;
  const asset = collateralAddress
    ? getCollateralAssetByAddress(collateralAddress)
    : undefined;
  const precision = asset ? getCollateralPrecision(asset) : 2;
  return currency(human, { precision }).format({ symbol: " " });
}

// Moment for when the activity happened (deposit/fill/withdrawal time), not when
// the notification row was written — see getNotificationActivitySeconds.
export function activityMoment(notification: GetNotifications200Response) {
  return moment.unix(getNotificationActivitySeconds(notification) ?? 0);
}

export const FILTER_LABELS: { value: NotificationFilter; key: string; defaultLabel: string }[] = [
  { value: "all", key: "filter_all", defaultLabel: "All" },
  { value: "alerts", key: "filter_alerts", defaultLabel: "Alerts" },
  { value: "trades", key: "filter_trades", defaultLabel: "Trades" },
  { value: "account", key: "filter_account", defaultLabel: "Account" },
];

export function dayBucket(
  notification: GetNotifications200Response,
  translateAll: TFunction
): string {
  const m = activityMoment(notification);
  if (m.isSame(moment(), "day")) return translateAll("today", { defaultValue: "Today" });
  if (m.isSame(moment().subtract(1, "day"), "day")) {
    return translateAll("yesterday", { defaultValue: "Yesterday" });
  }
  if (m.isSame(moment(), "year")) return m.format("MMM D");
  return m.format("MMM D, YYYY");
}
